import { Box, Button, FormControl, MenuItem, Select } from "@mui/material";
import { SettlementPeriodSelector } from "Components/Shared/SettlementPeriodSelector";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useState } from "react";
import { useDownloadSettlementReportMutation } from "Hooks/useSettlementReportDownload";
import { usePartyLicensesQuery } from "Api/Queries/Licenses/usePartyLicensesQuery";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import type { Region } from "Infrastructure/Api/Api";
import type { SettlementUserRole } from "Types/settlements";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES =
  Resources.Components.Settlements.SettlementDownloadButton;

const HUNTER_ROLES: ReadonlyArray<SettlementUserRole> = [
  AccessRoleCodes.Hunter,
  AccessRoleCodes.HunterPlus,
];

type Props = {
  partyPublicID: string;
  userRole: SettlementUserRole;
  isReadOnly?: boolean;
};

export const SettlementDownloadSection = ({
  partyPublicID,
  userRole,
  isReadOnly = false,
}: Props) => {
  const [selectedSettlementPeriodID, setSelectedSettlementPeriodID] = useState<
    number | null
  >(null);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);

  const { t } = useResource();
  const { mutate: downloadReport, isPending } =
    useDownloadSettlementReportMutation();

  // A hunter report is scoped to a single market (amounts are in the market's currency),
  // so hunter roles must send a region. Merchants are single-market by definition.
  const isHunterReport = HUNTER_ROLES.includes(userRole);
  const { data: partyLicenses } = usePartyLicensesQuery(
    isHunterReport ? partyPublicID : undefined,
  );

  const licenseRegions = [
    ...new Set((partyLicenses?.items ?? []).map((license) => license.region)),
  ];
  const region = isHunterReport
    ? (selectedRegion ??
      (licenseRegions.length === 1 ? licenseRegions[0] : undefined))
    : undefined;
  const isRegionMissing = isHunterReport && !region;

  return (
    <Box display="flex" gap={2} alignItems="center">
      <SettlementPeriodSelector
        value={selectedSettlementPeriodID}
        onChange={setSelectedSettlementPeriodID}
        disabled={isReadOnly}
      />
      {isHunterReport && licenseRegions.length > 1 && (
        <FormControl sx={{ minWidth: 160 }}>
          <Select
            sx={{ height: ({ spacing }) => spacing(13) }}
            value={selectedRegion ?? ""}
            onChange={(e) => setSelectedRegion(e.target.value as Region)}
            displayEmpty
            disabled={isReadOnly}
          >
            <MenuItem value="" disabled>
              {t(COMPONENT_RESOURCES.RegionPlaceholder)}
            </MenuItem>
            {licenseRegions.map((licenseRegion) => (
              <MenuItem key={licenseRegion} value={licenseRegion}>
                {t(COMPONENT_RESOURCES.Regions[licenseRegion])}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Button
        sx={{
          height: ({ spacing }) => spacing(13),
          "&.Mui-disabled": {
            color: ({ palette }) => palette.common.white,
            background: ({ palette }) => palette.action.disabledBackground,
          },
        }}
        type="button"
        variant="contained"
        fullWidth
        disabled={
          isReadOnly ||
          !selectedSettlementPeriodID ||
          isRegionMissing ||
          isPending
        }
        onClick={() => {
          if (selectedSettlementPeriodID && !isRegionMissing) {
            downloadReport({
              partyPublicID,
              settlementPeriodID: selectedSettlementPeriodID,
              userRole,
              region,
            });
          }
        }}
        endIcon={isPending ? undefined : <FileDownloadOutlinedIcon />}
      >
        {isPending
          ? t(COMPONENT_RESOURCES.Downloading)
          : t(COMPONENT_RESOURCES.Download)}
      </Button>
    </Box>
  );
};
